import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useAppSelector } from '@/app/store/hooks';
import { getErrorMessage, normalizeError } from '@/core/error-handling/ApiErrorHandler';
import { logAuthEvent } from '@/core/logging/auditLogger';
import { ErrorBanner } from '@/shared/components/ErrorBanner';
import { authService } from '../../common/api/authService';
import { selectUser } from '../../common/store/authSlice';
import { meetsPasswordPolicy, PASSWORD_MAX_LENGTH } from '../../common/utils/passwordRules';
import { preventCopyProps } from '../../common/utils/preventCopy';
import { AuthField } from '../../common/components/AuthField';
import { PasswordChecklist } from '../../common/components/PasswordChecklist';
/*
  The form's own layout -- `.login-form` (column, gaps, full width) and its container rules -- is in auth.css, which
  only the sign-in screens import. Arriving through sign-in it was already loaded; after a refresh on any other page
  it never was, and the first-sign-in dialog drew its fields, checklist and button unspaced. Imported here so the
  form brings what it uses, wherever it opens.
*/
import '../../common/auth.css';
import '../../common/pages/passwordRecovery.css';

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password').max(PASSWORD_MAX_LENGTH),
    newPassword: z.string(),
    confirmPassword: z.string(),
  })
  .superRefine((values, context) => {
    if (!meetsPasswordPolicy(values.newPassword)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Meet all password requirements below',
        path: ['newPassword'],
      });
    } else if (values.newPassword === values.currentPassword) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Choose a password different from your current one',
        path: ['newPassword'],
      });
    }
    if (values.newPassword !== values.confirmPassword) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Both passwords must match',
        path: ['confirmPassword'],
      });
    }
  });

type Values = z.infer<typeof schema>;

/** The fields a server refusal may name, in form order. */
const FORM_FIELDS = ['currentPassword', 'newPassword', 'confirmPassword'] as const satisfies readonly (keyof Values)[];

interface ChangePasswordFormProps {
  /**
   * Called once the platform has accepted the new password. Return `true` to show "Password updated
   * successfully." and clear the form (the profile page); a caller that moves on instead returns nothing.
   */
  onChanged: () => boolean | undefined;
  /** Under the submit button: the page's "Back", the dialog's "Sign out". */
  footer?: ReactNode;
  /** Prefix for the field ids, so the page and the dialog never share one. */
  idPrefix?: string;
}

/**
 * The Change Password form: Enter Current Password, Enter New Password, Confirm New Password, the live
 * checklist, and Update Password.
 *
 * One form for both places it appears -- the page the profile menu opens and the dialog a first sign-in is
 * held in -- so the fields, the rules and the messages cannot drift between them. What happens after a
 * successful change is the caller's: the page stays, the dialog sends the user to sign in again.
 */
export function ChangePasswordForm({ onChanged, footer, idPrefix = 'pw-change' }: ChangePasswordFormProps) {
  const user = useAppSelector(selectUser);
  const [busy, setBusy] = useState(false);
  const [error, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setError: setFieldError,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });
  const newPassword = useWatch({ control, name: 'newPassword' });

  const submit = handleSubmit(async (values) => {
    if (!user) return;
    setFormError(null);
    setDone(false);
    setBusy(true);
    try {
      /* Exactly as typed: a password is never trimmed or otherwise altered on its way to the server. */
      await authService.changePassword(user.id, values);
      logAuthEvent('password.changed');
      if (onChanged() === true) {
        reset();
        setDone(true);
      }
    } catch (err: unknown) {
      /*
        A refusal is reported where it belongs. The platform names the field it refuses -- a wrong Old
        Password is a 400 with "The old password is incorrect." on `currentPassword` -- so that message goes
        under that field, and the form stays open with the session intact. A refusal naming no field of this
        form keeps the banner.
      */
      const apiError = normalizeError(err);
      let mapped = false;
      for (const field of FORM_FIELDS) {
        const message = apiError.details?.[field]?.[0];
        if (message) {
          setFieldError(field, { type: 'server', message }, { shouldFocus: !mapped });
          mapped = true;
        }
      }
      if (!mapped) setFormError(getErrorMessage(apiError));
    } finally {
      setBusy(false);
    }
  });

  return (
    <form className="login-form pw-recovery__form" onSubmit={submit} noValidate>
      <AuthField
        id={`${idPrefix}-current`}
        label="Enter Current Password"
        type="password"
        autoComplete="current-password"
        required
        error={errors.currentPassword?.message}
        {...register('currentPassword')}
      />
      <AuthField
        id={`${idPrefix}-new`}
        label="Enter New Password"
        type="password"
        autoComplete="new-password"
        required
        error={errors.newPassword?.message}
        {...register('newPassword')}
      />
      <AuthField
        id={`${idPrefix}-confirm`}
        label="Confirm New Password"
        type="password"
        autoComplete="new-password"
        required
        error={errors.confirmPassword?.message}
        {...register('confirmPassword')}
        {...preventCopyProps}
      />

      <PasswordChecklist password={newPassword} />

      {error ? <ErrorBanner message={error} /> : null}
      {done ? (
        <p className="pw-recovery__status pw-recovery__success" role="status">
          Password updated successfully.
        </p>
      ) : null}

      <button type="submit" className="btn btn--primary" disabled={busy || !meetsPasswordPolicy(newPassword)}>
        <span className="btn__label">{busy ? 'Updating…' : 'Update Password'}</span>
      </button>

      {footer}
    </form>
  );
}
